import type { CSSProperties } from "react";

export type ThemeMode = "light" | "dark";

export const THEME_COLOR_KEYS = [
  "background",
  "surface",
  "elevated",
  "text",
  "muted",
  "border",
  "accent",
  "success",
  "warning",
  "danger",
] as const;

export type ThemeColorKey = (typeof THEME_COLOR_KEYS)[number];

export interface ThemeColors {
  background: string;
  surface: string;
  elevated: string;
  text: string;
  muted: string;
  border: string;
  accent: string;
  success: string;
  warning: string;
  danger: string;
}

export interface ThemeChoice {
  customized: boolean;
  colors: ThemeColors;
}

export interface SavedTheme {
  id: string;
  name: string;
  mode: ThemeMode;
  colors: ThemeColors;
}

export interface AppearancePreferences {
  version: 1;
  active_mode: ThemeMode;
  light: ThemeChoice;
  dark: ThemeChoice;
  active_theme_id: string;
  themes: SavedTheme[];
}

export const LIGHT_THEME_COLORS: ThemeColors = {
  background: "#F3F6FA",
  surface: "#FFFFFF",
  elevated: "#F8FAFC",
  text: "#172033",
  muted: "#58687D",
  border: "#D9E1EA",
  accent: "#2563EB",
  success: "#15803D",
  warning: "#A15C00",
  danger: "#C62828",
};

// Restrained graphite surfaces and a controlled cobalt action color. The
// palette intentionally stays neutral enough for long production sessions.
export const DARK_THEME_COLORS: ThemeColors = {
  background: "#0E121B",
  surface: "#151B26",
  elevated: "#1C2431",
  text: "#F4F7FB",
  muted: "#A9B5C6",
  border: "#2C3747",
  accent: "#4F7CFF",
  success: "#35B96F",
  warning: "#D9952B",
  danger: "#E6626B",
};

export const DEFAULT_APPEARANCE: AppearancePreferences = {
  version: 1,
  active_mode: "light",
  light: { customized: false, colors: LIGHT_THEME_COLORS },
  dark: { customized: false, colors: DARK_THEME_COLORS },
  active_theme_id: "builtin-light",
  themes: [],
};

export const BUILT_IN_THEMES: readonly SavedTheme[] = [
  { id: "builtin-light", name: "Default Light", mode: "light", colors: LIGHT_THEME_COLORS },
  { id: "builtin-dark", name: "Default Dark", mode: "dark", colors: DARK_THEME_COLORS },
];

export const MAX_SAVED_THEMES = 20;

const HEX_COLOR = /^#[0-9A-F]{6}$/i;

export const SHARED_THEME_FORMAT = "ai-media-studio-theme";
export const SHARED_THEME_VERSION = 2;

export interface SharedThemeFile {
  format: typeof SHARED_THEME_FORMAT;
  version: typeof SHARED_THEME_VERSION;
  exported_at: string;
  theme: Omit<SavedTheme, "id">;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isThemeColors(value: unknown): value is ThemeColors {
  return (
    isRecord(value) &&
    THEME_COLOR_KEYS.every((key) => typeof value[key] === "string" && HEX_COLOR.test(value[key]))
  );
}

function isThemeChoice(value: unknown): value is ThemeChoice {
  return isRecord(value) && typeof value.customized === "boolean" && isThemeColors(value.colors);
}

function isLegacyAppearancePreferences(value: unknown): value is Omit<AppearancePreferences, "active_theme_id" | "themes"> {
  return (
    isRecord(value) &&
    value.version === 1 &&
    (value.active_mode === "light" || value.active_mode === "dark") &&
    isThemeChoice(value.light) &&
    isThemeChoice(value.dark)
  );
}

export function createSharedThemeFile(
  appearance: AppearancePreferences,
  exportedAt = new Date()
): SharedThemeFile {
  const active = activeTheme(appearance);
  return {
    format: SHARED_THEME_FORMAT,
    version: SHARED_THEME_VERSION,
    exported_at: exportedAt.toISOString(),
    theme: {
      name: active.name,
      mode: active.mode,
      colors: cloneColors(active.colors),
    },
  };
}

export function parseSharedThemeFile(content: string): Omit<SavedTheme, "id"> {
  let value: unknown;
  try {
    value = JSON.parse(content);
  } catch {
    throw new Error("This file is not valid JSON.");
  }

  if (!isRecord(value) || value.format !== SHARED_THEME_FORMAT) {
    throw new Error("This is not an AI Media Studio theme file.");
  }
  if (value.version === 1 && isLegacyAppearancePreferences(value.appearance)) {
    const appearance = normalizeAppearance(value.appearance);
    const theme = activeTheme(appearance);
    return { name: "Imported theme", mode: theme.mode, colors: cloneColors(theme.colors) };
  }
  if (value.version !== SHARED_THEME_VERSION) {
    throw new Error("This theme file version is not supported.");
  }
  if (
    !isRecord(value.theme) ||
    typeof value.theme.name !== "string" ||
    value.theme.name.trim().length < 1 ||
    (value.theme.mode !== "light" && value.theme.mode !== "dark") ||
    !isThemeColors(value.theme.colors)
  ) {
    throw new Error("This theme file is incomplete or contains invalid colors.");
  }

  return {
    name: value.theme.name.trim().slice(0, 40),
    mode: value.theme.mode,
    colors: cloneColors(value.theme.colors),
  };
}

function cloneColors(colors: ThemeColors): ThemeColors {
  return { ...colors };
}

function normalizeHex(value: unknown, fallback: string): string {
  if (typeof value !== "string" || !HEX_COLOR.test(value)) return fallback;
  return value.toUpperCase();
}

function normalizeColors(value: unknown, defaults: ThemeColors): ThemeColors {
  const input = value && typeof value === "object" ? (value as Partial<ThemeColors>) : {};
  return Object.fromEntries(
    THEME_COLOR_KEYS.map((key) => [key, normalizeHex(input[key], defaults[key])])
  ) as unknown as ThemeColors;
}

export function normalizeAppearance(value: unknown): AppearancePreferences {
  const input = value && typeof value === "object" ? (value as Partial<AppearancePreferences>) : {};
  const light = input.light && typeof input.light === "object" ? input.light : undefined;
  const dark = input.dark && typeof input.dark === "object" ? input.dark : undefined;

  const themes: SavedTheme[] = Array.isArray(input.themes)
    ? input.themes.slice(0, MAX_SAVED_THEMES).flatMap((candidate, index) => {
        if (!isRecord(candidate)) return [];
        const mode = candidate.mode === "dark" ? "dark" : candidate.mode === "light" ? "light" : null;
        const name = typeof candidate.name === "string" ? candidate.name.trim().slice(0, 40) : "";
        const id = typeof candidate.id === "string" && /^[a-zA-Z0-9_-]{3,80}$/.test(candidate.id)
          ? candidate.id
          : `theme-${index + 1}`;
        if (!mode || !name) return [];
        return [{ id, name, mode, colors: normalizeColors(candidate.colors, defaultColorsFor(mode)) }];
      })
    : [];

  // Older preferences stored one editable palette per mode. Promote those
  // palettes to named themes once, keeping deterministic IDs for idempotence.
  if (!Array.isArray(input.themes)) {
    if (light?.customized === true) {
      themes.push({
        id: "legacy-light",
        name: "My Light",
        mode: "light",
        colors: normalizeColors(light.colors, LIGHT_THEME_COLORS),
      });
    }
    if (dark?.customized === true) {
      themes.push({
        id: "legacy-dark",
        name: "My Dark",
        mode: "dark",
        colors: normalizeColors(dark.colors, DARK_THEME_COLORS),
      });
    }
  }

  const fallbackId = input.active_mode === "dark" ? "builtin-dark" : "builtin-light";
  const requestedId = typeof input.active_theme_id === "string" ? input.active_theme_id : null;
  const validIds = new Set([...BUILT_IN_THEMES.map((theme) => theme.id), ...themes.map((theme) => theme.id)]);
  const legacyActiveId = input.active_mode === "dark" && dark?.customized === true
    ? "legacy-dark"
    : input.active_mode !== "dark" && light?.customized === true
      ? "legacy-light"
      : fallbackId;
  const activeThemeId = requestedId && validIds.has(requestedId) ? requestedId : legacyActiveId;
  const selected = [...BUILT_IN_THEMES, ...themes].find((theme) => theme.id === activeThemeId);

  return {
    version: 1,
    active_mode: selected?.mode ?? "light",
    light: {
      customized: light?.customized === true,
      colors: normalizeColors(light?.colors, LIGHT_THEME_COLORS),
    },
    dark: {
      customized: dark?.customized === true,
      colors: normalizeColors(dark?.colors, DARK_THEME_COLORS),
    },
    active_theme_id: activeThemeId,
    themes,
  };
}

export function cloneAppearance(value: AppearancePreferences): AppearancePreferences {
  const normalized = normalizeAppearance(value);
  return {
    version: 1,
    active_mode: normalized.active_mode,
    light: { customized: normalized.light.customized, colors: cloneColors(normalized.light.colors) },
    dark: { customized: normalized.dark.customized, colors: cloneColors(normalized.dark.colors) },
    active_theme_id: normalized.active_theme_id,
    themes: normalized.themes.map((theme) => ({ ...theme, colors: cloneColors(theme.colors) })),
  };
}

export function defaultColorsFor(mode: ThemeMode): ThemeColors {
  return cloneColors(mode === "dark" ? DARK_THEME_COLORS : LIGHT_THEME_COLORS);
}

export function activeThemeColors(appearance: AppearancePreferences): ThemeColors {
  return activeTheme(appearance).colors;
}

export function allThemes(appearance: AppearancePreferences): SavedTheme[] {
  const normalized = normalizeAppearance(appearance);
  return [
    ...BUILT_IN_THEMES.map((theme) => ({ ...theme, colors: cloneColors(theme.colors) })),
    ...normalized.themes.map((theme) => ({ ...theme, colors: cloneColors(theme.colors) })),
  ];
}

export function activeTheme(appearance: AppearancePreferences): SavedTheme {
  const normalized = normalizeAppearance(appearance);
  return allThemesWithoutNormalize(normalized).find((theme) => theme.id === normalized.active_theme_id)
    ?? { ...BUILT_IN_THEMES[0], colors: cloneColors(BUILT_IN_THEMES[0].colors) };
}

function allThemesWithoutNormalize(appearance: AppearancePreferences): SavedTheme[] {
  return [...BUILT_IN_THEMES, ...appearance.themes];
}

export function selectTheme(appearance: AppearancePreferences, themeId: string): AppearancePreferences {
  const next = cloneAppearance(appearance);
  const selected = allThemesWithoutNormalize(next).find((theme) => theme.id === themeId);
  if (!selected) return next;
  next.active_theme_id = selected.id;
  next.active_mode = selected.mode;
  return next;
}

export function createThemeId(): string {
  const suffix = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `theme-${suffix}`;
}

export function isBuiltInTheme(themeId: string): boolean {
  return BUILT_IN_THEMES.some((theme) => theme.id === themeId);
}

type RGB = [number, number, number];

function hexToRgb(hex: string): RGB {
  const value = normalizeHex(hex, "#000000").slice(1);
  return [
    Number.parseInt(value.slice(0, 2), 16),
    Number.parseInt(value.slice(2, 4), 16),
    Number.parseInt(value.slice(4, 6), 16),
  ];
}

function rgbToHex([red, green, blue]: RGB): string {
  return `#${[red, green, blue]
    .map((channel) => Math.round(channel).toString(16).padStart(2, "0"))
    .join("")}`.toUpperCase();
}

function mixHex(base: string, overlay: string, overlayWeight: number): string {
  const a = hexToRgb(base);
  const b = hexToRgb(overlay);
  const weight = Math.max(0, Math.min(1, overlayWeight));
  return rgbToHex(a.map((channel, index) => channel * (1 - weight) + b[index] * weight) as RGB);
}

function channelLuminance(channel: number): number {
  const value = channel / 255;
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

export function contrastRatio(foreground: string, background: string): number {
  const [red, green, blue] = hexToRgb(foreground);
  const luminance =
    0.2126 * channelLuminance(red) +
    0.7152 * channelLuminance(green) +
    0.0722 * channelLuminance(blue);
  const [bgRed, bgGreen, bgBlue] = hexToRgb(background);
  const backgroundLuminance =
    0.2126 * channelLuminance(bgRed) +
    0.7152 * channelLuminance(bgGreen) +
    0.0722 * channelLuminance(bgBlue);
  const light = Math.max(luminance, backgroundLuminance);
  const dark = Math.min(luminance, backgroundLuminance);
  return (light + 0.05) / (dark + 0.05);
}

function readableForeground(background: string): string {
  return contrastRatio("#FFFFFF", background) >= contrastRatio("#0B0F17", background)
    ? "#FFFFFF"
    : "#0B0F17";
}

export interface ContrastIssue {
  color: "text" | "muted" | "success" | "warning" | "danger";
  ratio: number;
  minimum: number;
  message: string;
}

const CONTRAST_KEYS: ContrastIssue["color"][] = ["text", "muted", "success", "warning", "danger"];

export function findContrastIssues(colors: ThemeColors): ContrastIssue[] {
  const surfaces = [colors.background, colors.surface, colors.elevated];
  return CONTRAST_KEYS.flatMap((color) => {
    const ratio = Math.min(...surfaces.map((surface) => contrastRatio(colors[color], surface)));
    if (ratio >= 4.5) return [];
    return [{
      color,
      ratio,
      minimum: 4.5,
      message: `${color[0].toUpperCase()}${color.slice(1)} needs 4.5:1 contrast across the main surfaces.`,
    }];
  });
}

function adjustAgainstSurfaces(foreground: string, surfaces: string[], target: number): string {
  if (surfaces.every((surface) => contrastRatio(foreground, surface) >= target)) return foreground;

  let closestValid: { color: string; distance: number } | null = null;
  let strongestFallback: { color: string; floor: number } | null = null;
  for (const destination of ["#000000", "#FFFFFF"]) {
    for (let step = 1; step <= 100; step += 1) {
      const candidate = mixHex(foreground, destination, step / 100);
      const ratios = surfaces.map((surface) => contrastRatio(candidate, surface));
      const floor = Math.min(...ratios);
      if (floor >= target) {
        if (!closestValid || step < closestValid.distance) {
          closestValid = { color: candidate, distance: step };
        }
        break;
      }
      if (!strongestFallback || floor > strongestFallback.floor) {
        strongestFallback = { color: candidate, floor };
      }
    }
  }
  return closestValid?.color ?? strongestFallback?.color ?? foreground;
}

export function autoFixContrast(colors: ThemeColors): ThemeColors {
  const next = cloneColors(colors);
  const surfaces = [next.background, next.surface, next.elevated];
  for (const key of CONTRAST_KEYS) {
    next[key] = adjustAgainstSurfaces(next[key], surfaces, 4.5);
  }
  return next;
}

function rgbChannels(hex: string): string {
  return hexToRgb(hex).join(" ");
}

export type ThemeCssVariables = Record<`--studio-${string}`, string>;

export function themeCssVariables(mode: ThemeMode, colors: ThemeColors): ThemeCssVariables {
  const toward = mode === "dark" ? "#FFFFFF" : "#000000";
  const accentForeground = readableForeground(colors.accent);
  const dangerForeground = readableForeground(colors.danger);
  const variables: Record<string, string> = {
    bg: colors.background,
    panel: colors.surface,
    card: colors.elevated,
    "card-hover": mixHex(colors.elevated, colors.text, mode === "dark" ? 0.07 : 0.035),
    border: colors.border,
    "border-light": mixHex(colors.border, colors.text, mode === "dark" ? 0.2 : 0.16),
    ink: colors.text,
    muted: colors.muted,
    faint: colors.muted,
    accent: colors.accent,
    "accent-hover": mixHex(colors.accent, toward, 0.12),
    "accent-fg": accentForeground,
    "accent-soft": mixHex(colors.background, colors.accent, mode === "dark" ? 0.18 : 0.09),
    success: colors.success,
    "success-soft": mixHex(colors.background, colors.success, mode === "dark" ? 0.17 : 0.09),
    warning: colors.warning,
    "warning-soft": mixHex(colors.background, colors.warning, mode === "dark" ? 0.17 : 0.09),
    "warning-border": mixHex(colors.border, colors.warning, 0.46),
    "warning-ink": colors.warning,
    "info-soft": mixHex(colors.background, colors.accent, mode === "dark" ? 0.14 : 0.065),
    "info-border": mixHex(colors.border, colors.accent, 0.38),
    danger: colors.danger,
    "danger-fg": dangerForeground,
    shadow: colors.text,
    scrollbar: mixHex(colors.border, colors.text, mode === "dark" ? 0.15 : 0.1),
    "scrollbar-hover": colors.muted,
  };

  return Object.fromEntries(
    Object.entries(variables).map(([key, value]) => [`--studio-${key}`, rgbChannels(value)])
  ) as ThemeCssVariables;
}

export type ThemePreviewStyle = CSSProperties & ThemeCssVariables;

export function themePreviewStyle(mode: ThemeMode, colors: ThemeColors): ThemePreviewStyle {
  return {
    ...themeCssVariables(mode, colors),
    colorScheme: mode,
  };
}

const CSS_VARIABLE_NAMES = Object.keys(
  themeCssVariables("light", LIGHT_THEME_COLORS)
) as Array<keyof ThemeCssVariables>;

export interface ThemeCache {
  mode: ThemeMode;
  variables: ThemeCssVariables;
}

export function applyAppearanceToRoot(appearance: AppearancePreferences): ThemeCache {
  const root = document.documentElement;
  const selected = activeTheme(appearance);
  const mode = selected.mode;
  const variables = themeCssVariables(mode, selected.colors);
  root.dataset.theme = mode;
  root.style.colorScheme = mode;
  for (const [name, value] of Object.entries(variables)) root.style.setProperty(name, value);
  return { mode, variables };
}

export function resetThemeRoot(): void {
  const root = document.documentElement;
  root.dataset.theme = "light";
  root.style.colorScheme = "light";
  for (const name of CSS_VARIABLE_NAMES) root.style.removeProperty(name);
}
